import type { AccoDetailsAPIResponse, NearbyAccosAPIResponse } from './types'
import {
  createQueryStringSerializer,
  type QueryStringSerializer,
} from '@/scopes/common/infrastructure/query-strings/queryStringSerializer'
import defaultFetch from 'isomorphic-unfetch'
import { type AccoDetailsResponseMapper, createAccoDetailsResponseMapper } from './accoDetailsResponseMapper'
import { createDestinationMapResponseMapper, type DestinationMapResponseMapper } from './destinationMapResponseMapper'

interface Dependencies {
  env: {
    NEXT_PUBLIC_API_BASE_URL: string
  }
  fetch: typeof defaultFetch
  queryStringSerializer: QueryStringSerializer
  destinationMapResponseMapper: DestinationMapResponseMapper
  accoDetailsResponseMapper: AccoDetailsResponseMapper
}

export interface GetDestinationMapParameters {
  currentAccoId: string
}

const options: RequestInit = {
  referrerPolicy: 'strict-origin-when-cross-origin' as ReferrerPolicy,
  headers: {
    'Accept': 'application/json',
    'Content-Type': 'application/json',
  },
}

export function createDestinationMapAPIClient(dependencies: Partial<Dependencies> = {}) {
  const {
    env = {
      NEXT_PUBLIC_API_BASE_URL: process.env.NEXT_PUBLIC_API_BASE_URL,
    },
    fetch = defaultFetch,
    queryStringSerializer = createQueryStringSerializer(),
    destinationMapResponseMapper = createDestinationMapResponseMapper(),
    accoDetailsResponseMapper = createAccoDetailsResponseMapper(),
  } = dependencies as Dependencies

  async function getNearbyAccos({ currentAccoId }: GetDestinationMapParameters) {
    const queryString = queryStringSerializer.serialize({ accommodationId: currentAccoId })

    const response = await fetch(
      `${env.NEXT_PUBLIC_API_BASE_URL}/AccommodationGeoApi/GetNearbyAccommodationsByAccommodationId?${queryString}`,
      options,
    )

    const data = (await response.json()) as NearbyAccosAPIResponse
    return destinationMapResponseMapper.map(data)
  }

  async function getAccoDetails({ accoId }: { accoId: string }) {
    const queryString = queryStringSerializer.serialize({ accommodationId: accoId })

    const response = await fetch(
      `${env.NEXT_PUBLIC_API_BASE_URL}/AccommodationGeoApi/GetAccommodationDetailsById?${queryString}`,
      options,
    )

    const data = (await response.json()) as AccoDetailsAPIResponse[]

    return accoDetailsResponseMapper.map(data[0])
  }

  async function getAccosByCountry({ countryId }: { countryId: string }) {
    const queryString = queryStringSerializer.serialize({ countryId })

    const response = await fetch(
      `${env.NEXT_PUBLIC_API_BASE_URL}/AccommodationGeoApi/GetAccommodationsByCountry?${queryString}`,
      options,
    )

    const data = (await response.json())
    return destinationMapResponseMapper.map(data)
  }

  async function getAccosByRegion({ regionId }: { regionId: string }) {
    const queryString = queryStringSerializer.serialize({ regionId })

    const response = await fetch(
      `${env.NEXT_PUBLIC_API_BASE_URL}/AccommodationGeoApi/GetAccommodationsByRegion?${queryString}`,
      options,
    )

    const data = (await response.json())
    return destinationMapResponseMapper.map(data)
  }

  return { getNearbyAccos, getAccoDetails, getAccosByCountry, getAccosByRegion }
}
